import FormValidator from '../util/FormValidator';

export default class LabelForm {
  /**
   * Label form validator
   *
   * @param {HTMLElement} form Form element
   * @property {Boolean} isValid Validation status of the whole form
   * @property {Array} errors Validation errors
   */
  constructor(form) {
    this.form = form;
    this.isValid = false;
    this.errors = [];
    this.initialize(form);
  }

  initialize() {
    this.steps = this.form.querySelectorAll('.form-step');
    this.validator = new FormValidator(this.form);
    this.formAnchor = document.getElementById('get-the-label');
    this.stepInfo = document.getElementById('get-the-label-steps');
    this.stepInfoTransitions = 1000; // Step info transitions duration in ms
    this.collapseDuration = 500; // Collapse transition duration in ms
    this.scrollDuration = 750; // Scroll tween duration in ms

    /** Bind events and linked fiends */
    this.bindStepsButtonsEvents();
    this.bindTextareasResizeEvent();
    this.bindLinkedFields();
    for (const input of this.steps[0].querySelectorAll('input[name="affiliation"')) {
      /** Submit first step on click on an affiliation image-radio */
      input.addEventListener('click', () => {
        this.steps[0].querySelector('.form-button-next').click();
      });
    }

    /** Remove built-in validation */
    this.form.setAttribute('novalidate', 'true');
  }

  /**
   * Bind the buttons events for each step
   *
   * Scroll to the step info container first, process the buttons actions and then scroll back to the form content
   */
  bindStepsButtonsEvents() {
    for (const step of this.steps) {
      for (const button of step.querySelectorAll('.form-button')) {
        /** Bind the next / prev buttons events */
        button.addEventListener('click', e => {
          e.preventDefault();
          const button = e.currentTarget;
          const currentStep = parseInt(button.closest('.step').getAttribute('data-step'), 10) - 1;
          const offset = document.getElementById('head').getBoundingClientRect().height; /** Scroll offset value */

          /** Scroll to step info container */
          window.scroll(0, this.stepInfo.getBoundingClientRect().top + window.pageYOffset - offset);

          /** Scroll tween delay */
          setTimeout(() => {
            /** Next step */
            if (button.classList.contains('form-button-next')) {
              this.nextStep(step, currentStep);
            }
            /** Previous step */
            if (button.classList.contains('form-button-prev')) {
              this.prevStep(currentStep);
            }

            /** Whole animation transitions delay */
            setTimeout(() => {
              /** Scroll back to step content */
              let anchor = this.formAnchor.getBoundingClientRect().top;
              if (this.validator.errors.length > 0) {
                const firstErrorElt = step.querySelector('[class*="is-invalid"]');
                if (button.classList.contains('form-button-next') && firstErrorElt !== null) {
                  anchor = firstErrorElt.getBoundingClientRect().top - 15;
                }
              }
              window.scroll(0, anchor + window.pageYOffset - offset);
            }, this.stepInfoTransitions + this.collapseDuration);
          }, this.scrollDuration);
        });
      }
    }
  }

  /**
   * Reset the current step height on a textarea vertical resize
   */
  bindTextareasResizeEvent() {
    for (const textarea of this.form.querySelectorAll('textarea')) {
      textarea.setAttribute('data-height', textarea.clientHeight);
      textarea.addEventListener('mouseup', e => {
        const currentStep = parseInt(e.currentTarget.closest('.step').getAttribute('data-step'), 10) - 1;
        if (parseInt(textarea.getAttribute('data-height'), 10) !== textarea.clientHeight) {
          textarea.setAttribute('data-height', textarea.clientHeight);
          window.collapsable.defineElementHeight(this.form.querySelector(`[data-step='${currentStep + 1}']`));
        }
      });
    }
  }

  /**
   * Bind pairs of fields that must be synchronized
   */
  bindLinkedFields() {
    /** Radio buttons */
    for (const radioGroup of this.form.querySelectorAll('[data-linked-radio]')) {
      for (const radio of radioGroup.querySelectorAll('input[type="radio"]')) {
        radio.addEventListener('change', e => {
          if (e.currentTarget.checked) {
            const linkedRadios = this.form.querySelectorAll(`[name='${radioGroup.getAttribute('data-linked-radio')}']`);
            for (const linkedRadio of linkedRadios) {
              if (linkedRadio.value === e.currentTarget.value) {
                linkedRadio.checked = true;
                break;
              }
            }
          }
        });
      }
    }
  }

  /**
   * Validate the current step and display the next step
   * or mark the current step as invalid and show it again
   *
   * @param {HTMLElement} step
   * @param {number} currentStep
   */
  nextStep(step, currentStep) {
    const currentStepElt = this.stepInfo.querySelector(`.label-steps-list li:nth-child(${currentStep + 1})`);

    /** If the form step is valid */
    if (this.validator.validateFields(step.querySelectorAll(this.validator.query))) {
      /** Mark the current step as valid */
      currentStepElt.classList.add('is-valid');
      currentStepElt.classList.remove('is-invalid');

      if (currentStep + 1 < this.steps.length) {
        /** Mark the next step as active in step info */
        const nextStepElt = this.stepInfo.querySelector(`.label-steps-list li:nth-child(${currentStep + 2})`);
        nextStepElt.classList.add('is-active');

        /** Expand the next step */
        setTimeout(() => {
          this.steps[currentStep + 1].classList.remove('is-collapsed');
        }, this.stepInfoTransitions);
      } else {
        /** Submit the form */
        this.submitForm(step);
      }

      /** Populate the form summary */
      if (currentStep + 1 === this.steps.length - 1) {
        this.populateSummary();
        window.collapsable.defineElementHeight(this.form.querySelector(`[data-step='${currentStep + 2}']`));
      }
    } else {
      /** Mark the current step as invalid in step info */
      currentStepElt.classList.add('is-invalid');
      currentStepElt.classList.remove('is-valid');

      /** Expand the current step */
      setTimeout(() => {
        step.classList.remove('is-collapsed');
      }, this.stepInfoTransitions);

      console.error(this.validator.errors);
    }
  }

  /**
   * Display previous step
   *
   * @param {number} currentStep
   */
  prevStep(currentStep) {
    if (currentStep > 0) {
      const currentStepElt = this.stepInfo.querySelector(`.label-steps-list li:nth-child(${currentStep + 1})`);
      const prevStepElt = this.stepInfo.querySelector(`.label-steps-list li:nth-child(${currentStep})`);

      /** Remove validation status of the step and of the previous step in step info */
      currentStepElt.classList.remove('is-active');
      currentStepElt.classList.remove('is-invalid');
      prevStepElt.classList.remove('is-valid');

      /** Expand the previous step */
      setTimeout(() => {
        this.steps[currentStep - 1].classList.remove('is-collapsed');
      }, this.stepInfoTransitions);
    }
  }

  /**
   * Populate the form summary elements identified by data-field-name attribute
   */
  populateSummary() {
    for (const summaryField of this.form.querySelectorAll('[data-field-name]')) {
      const input = this.form.querySelector(`[name='${summaryField.getAttribute('data-field-name')}']`);
      let text = input.value.trim();

      /** Get the text for radio buttons */
      if (input.value === 'oui' || input.value === 'non') {
        const attributeValue = this.form.getAttribute(`data-txt-${input.value}`);
        console.log(`Field: ${summaryField.getAttribute('data-field-name')}, Input value: ${input.value}, Attribute value: ${attributeValue}`);

        // Debug: Check if this is a radio button and what's actually selected
        if (input.type === 'radio') {
          const radioGroup = input.closest('.form-radio-group');
          const selectedRadio = radioGroup ? radioGroup.querySelector(`input[name='${input.name}']:checked`) : null;
          console.log('Radio group for ' + input.name + ':', radioGroup);
          console.log('Selected radio:', selectedRadio);
          if (selectedRadio) {
            console.log('Selected radio value: ' + selectedRadio.value);
            text = this.form.getAttribute(`data-txt-${selectedRadio.value}`) || selectedRadio.value;
          }
        } else if (attributeValue !== null && attributeValue !== '') {
          text = attributeValue;
        }
      }
      if (text.length > 0) {
        /** Set the text for a table display or any other tag*/
        if (summaryField.tagName.toLowerCase() === 'tr') {
          summaryField.querySelector(':first-child').innerHTML = this.validator.getFieldLabel(input);
          summaryField.querySelector(':last-child').innerHTML = this.nl2br(text);
        } else {
          summaryField.innerHTML = this.nl2br(text);
        }
        /** Show the summary field */
        summaryField.removeAttribute('hidden');
      } else {
        /** Hide the summary field */
        summaryField.setAttribute('hidden', 'true');
      }
    }
  }

  /**
   * Submit the form
   *
   * @param {HTMLElement} step
   */
  submitForm(step) {
    if (this.validator.validateForm()) {
      /** Check the captcha and send the form */
      this.checkCaptchaAndSend(step);
    } else {
      /** Expand the current step */
      setTimeout(() => {
        step.classList.remove('is-collapsed');
      }, this.stepInfoTransitions);

      console.error(this.validator.errors);
    }
  }

  /**
   * Check Google reCaptcha validity
   *
   * @param {HTMLElement} step
   */
  checkCaptchaAndSend(step) {
    this.showSendingStatus();

    if (this.formHasUploads()) {
      this.sendFormWithStagedUpload(step);
      return;
    }

    this.getRecaptchaToken()
      .then(token => this.sendForm(step, token))
      .catch(() => this.showFormError(step, this.form.getAttribute('data-txt-error-recaptcha')));
  }

  formHasUploads() {
    for (const input of this.form.querySelectorAll('input[type="file"]')) {
      if (!input.files) {
        continue;
      }
      for (const file of input.files) {
        if (file.size > 0) {
          return true;
        }
      }
    }
    return false;
  }

  getRecaptchaToken() {
    const reCaptchaKey = this.form.getAttribute('data-recaptcha-key');
    const requireRecaptcha = this.form.getAttribute('data-require-recaptcha') === '1';

    if (!reCaptchaKey || reCaptchaKey.length === 0) {
      return requireRecaptcha ? Promise.reject() : Promise.resolve(undefined);
    }

    if (!window.grecaptcha || typeof window.grecaptcha.execute !== 'function') {
      return Promise.reject();
    }

    return new Promise((resolve, reject) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(reCaptchaKey, { action: 'label' })
          .then(resolve)
          .catch(reject);
      });
    });
  }

  getCsrfToken() {
    const field = this.form.querySelector('input[name="csrf-token"]');
    return field ? field.value : '';
  }

  postFormData(formData) {
    const url = this.form.getAttribute('action');
    const headers = new Headers({
      Accept: 'application/json',
      'X-CSRF-TOKEN': this.getCsrfToken(),
    });

    return fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    }).then(response => {
      if (!response.ok) {
        throw new Error(`${response.status}: ${response.statusText}`);
      }
      return response.json();
    });
  }

  sendFormWithStagedUpload(step) {
    const prepareData = new FormData(this.form);
    prepareData.append('mil_form_stage', 'prepare');

    this.postFormData(prepareData)
      .then(stageResponse => {
        if (!stageResponse || !stageResponse.staged || !stageResponse.stage_key) {
          throw new Error(
            (stageResponse && stageResponse.message) || this.form.getAttribute('data-txt-error')
          );
        }

        return this.getRecaptchaToken().then(token => {
          const commitData = new FormData();
          commitData.append('csrf-token', this.getCsrfToken());
          commitData.append('mil_form_stage', 'commit');
          commitData.append('mil_stage_key', stageResponse.stage_key);
          commitData.append('token', token);
          return this.postFormData(commitData);
        });
      })
      .then(data => this.onSenderResponse({ sent: true, data }, step))
      .catch(error => {
        this.onSenderResponse(
          {
            sent: false,
            error: error.message,
            data: { error: 'request_failed', message: error.message },
          },
          step
        );
      });
  }

  showSendingStatus() {
    const formStatus = this.form.querySelector('.form-status');
    formStatus.innerHTML = this.form.getAttribute('data-txt-sending');
    formStatus.setAttribute('data-status', 'sent');
    window.collapsable.defineElementHeight(formStatus.closest('.collapsable'));
    setTimeout(() => {
      formStatus.closest('.collapsable').classList.remove('is-collapsed');
    }, 10);
  }

  showFormError(step, message) {
    const formStatus = this.form.querySelector('.form-status');
    formStatus.innerHTML = message || this.form.getAttribute('data-txt-error');
    formStatus.setAttribute('data-status', 'error');
    window.collapsable.defineElementHeight(formStatus.closest('.collapsable'));
    setTimeout(() => {
      formStatus.closest('.collapsable').classList.remove('is-collapsed');
      step.classList.remove('is-collapsed');
    }, 10);
  }

  /**
   * Send the form (no file uploads — token is fetched immediately before POST).
   *
   * @param {HTMLElement} step
   * @param {String} token
   */
  sendForm(step, token) {
    const formData = new FormData(this.form);
    if (token !== undefined) {
      formData.append('token', token);
    }

    this.postFormData(formData)
      .then(data => this.onSenderResponse({ sent: true, data }, step))
      .catch(error => {
        this.onSenderResponse(
          {
            sent: false,
            error: error.message,
            data: { error: 'request_failed', message: error.message },
          },
          step
        );
      });
  }

  /**
   * Process the form sender response
   * @param {Response} response
   * @param {HTMLElement} step
   */
  onSenderResponse(response, step) {
    const formStatus = this.form.querySelector('.form-status');
    formStatus.closest('.collapsable').classList.add('is-collapsed');

    setTimeout(() => {
      if (response.sent && response.data.sent && undefined === response.error && undefined === response.data.error) {
        /** Set the confirmation message */
        formStatus.innerHTML = this.form.getAttribute('data-txt-sent');
        formStatus.setAttribute('data-status', 'sent');

        /** Destroy the validator */
        this.validator.destroy();
      } else {
        /** Mark the current step as invalid in step info */
        const currentStep = parseInt(step.getAttribute('data-step'), 10) - 1;
        const currentStepElt = this.stepInfo.querySelector(`.label-steps-list li:nth-child(${currentStep + 1})`);
        currentStepElt.classList.add('is-invalid');
        currentStepElt.classList.remove('is-valid');

        /** Set the error message (prefer server message, e.g. reCAPTCHA expiry) */
        const serverMessage =
          (response.data && response.data.message) ||
          response.message ||
          this.form.getAttribute('data-txt-error');
        formStatus.innerHTML = serverMessage;
        formStatus.setAttribute('data-status', 'error');

        /** Expand the current step */
        setTimeout(() => {
          step.classList.remove('is-collapsed');
        }, this.stepInfoTransitions);

        console.error(response.error, response.data.error);
      }
      /** Expand the form status box */
      window.collapsable.defineElementHeight(formStatus.closest('.collapsable'));
      setTimeout(() => {
        formStatus.closest('.collapsable').classList.remove('is-collapsed');
      }, 10);
    }, this.collapseDuration);
  }

  /**
   * Inserts HTML line breaks before all newlines in a string
   * @param {String} str
   * @returns {String}
   */
  nl2br(str) {
    return str.replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1<br />$2');
  }
}



// WEBPACK FOOTER //
// ./scripts/components/LabelForm.js